import { type LanguageListItem, type LanguageListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React from 'react';

import { LanguageFormDrawer } from '../components/LanguageFormDrawer';
import { listLanguages } from '../languages.api';
import {
  useBulkDeleteLanguages,
  useBulkSetLanguagesStatus,
  useDeleteLanguage,
  useLanguagesList,
} from '../use-languages';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

export const LanguagesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteLanguage = useDeleteLanguage();
  const bulkDeleteMut = useBulkDeleteLanguages();
  const bulkSetStatusMut = useBulkSetLanguagesStatus();

  const columns: ListScreenColumns<LanguageListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<LanguageListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'nativeName',
        header: 'Native script',
        cell: (row) => <span className="text-zinc-600">{row.nativeName ?? '-'}</span>,
      },
      {
        id: 'sortOrder',
        header: 'Sort',
        sortable: true,
        width: '80px',
        cell: (row) => <span>{row.sortOrder}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<LanguageListItem, LanguageListQuery>
      title="Languages"
      description="Guide / driver language master. Used by the Activity and Transfer pickers to filter by spoken language."
      useList={useLanguagesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      toQuery={(state, base) => {
        const status = state.filters.status;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add language' }}
      renderDrawer={({ open, mode, row, close }) => (
        <LanguageFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          language={row}
        />
      )}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteLanguage.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this language?',
            description: `${row.name} will be soft-deleted. Rows already referencing it are unaffected.`,
          }),
        },
      }}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} language${rows.length === 1 ? '' : 's'}?`,
            description: 'These languages will be soft-deleted.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} language${rows.length === 1 ? '' : 's'}?`,
            description: isActive
              ? 'They will become selectable again in activity and transfer pickers.'
              : 'They will be hidden from new selections but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listLanguages(q),
        filename: 'languages',
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'Native name', value: (r) => r.nativeName ?? '' },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
        ],
      }}
      copy={{
        searchPlaceholder: 'Search by code, name, or native name',
        emptyTitle: 'No languages match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load languages',
        errorDescription: 'The server returned an error. Try refreshing.',
      }}
    />
  );
};
